import axios from 'axios'
import { storage } from './storage'

/**
 * Get backend URL from runtime config, build-time env, or default
 * Priority: Runtime config (Docker Compose env) > Build-time env > Default (/api)
 */
export const getBackendUrl = (): string => {
  if (import.meta.env.DEV) {
    return '/api'
  }
  
  // Try runtime config first (from Docker Compose environment variable)
  if (typeof window !== 'undefined' && (window as any).__APP_CONFIG__?.VITE_BACKEND_URL) {
    return (window as any).__APP_CONFIG__.VITE_BACKEND_URL
  }
  
  // Fallback to build-time env variable
  if (import.meta.env.VITE_BACKEND_URL) {
    return import.meta.env.VITE_BACKEND_URL
  }
  
  // Default fallback
  return '/api'
}

const API_URL = getBackendUrl()

// Create a single shared axios instance for the entire application
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout for large file uploads
  maxContentLength: Infinity, // No limit on response size
  maxBodyLength: Infinity, // No limit on request size (important for file uploads)
})

// Add token to all requests if available
api.interceptors.request.use((config) => {
  const token = storage.getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 responses (token expired/invalid) globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page or if this is a login request
      const isLoginPage = window.location.pathname === '/login'
      const isLoginRequest = error.config?.url?.includes('/auth/login')
      
      if (!isLoginPage && !isLoginRequest) {
        console.warn('Token expired or invalid - logging out')
        storage.clearAuth()
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Kura çekilirken sekme kapanırsa backend'e çekimi durdurmasını bildirir.
 * navigator.sendBeacon Authorization header gönderemediği için keepalive fetch kullanılır:
 * sayfa kapanırken de tamamlanır ve token taşır. Kullanıcı bilgisi backend'de token'dan alınır.
 */
export const notifyStopDrawOnClose = (kuraId: string | number): void => {
  const token = storage.getToken()
  if (!token) return

  void fetch(`${API_URL}/draw/${kuraId}/stop-draw-onclose?reason=tab_closed`, {
    method: 'POST',
    keepalive: true,
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {})
}
